import { Component, OnInit, Input, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AssetService } from '@app/_services/asset.service';
import { LocationService } from '@app/_services/location.service';
import { DepartmentService } from '@app/_services/department.service';
import { LegalEntityService } from '@app/_services/legalEntity.service';
import { EmployeeService } from '@app/_services/employee.service';
import { NzMessageService, NzDrawerRef } from 'ng-zorro-antd';
import { Location } from '@app/_models/_DTOs/location';
import { Department } from '@app/_models/_DTOs/department';
import { LegalEntity } from '@app/_models/_DTOs/legalEntity';
import { Employee } from '@app/_models/_DTOs/employee';
import { AssetUnassignmentViewModel } from '@app/_models/_viewModels/assetUnassignment.viewmodel';
import { DocumentService } from '@app/_services/document.service';
import { ResizeService } from '@app/_services/resize.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-asset-unassignment',
  templateUrl: './asset-unassignment.component.html',
  styleUrls: ['./asset-unassignment.component.css']
})
export class AssetUnassignmentComponent implements OnInit, AfterViewInit {
  @ViewChild('editDrawer', { static: false }) editDrawer: NzDrawerRef;

  @Input() assetViewModel: AssetUnassignmentViewModel; // Input is the view model with assets to unassign
  @Input() selectedLegalEntityId$: Subject<number>;
  @Input() isEditing: boolean;
  @Input() isVisible: boolean;
  @Output() drawerClosed = new EventEmitter<boolean>();

  selectedLegalEntityId: number | null = null;

  private destroy$ = new Subject<void>();



  public createForm: NgForm;
  drawerWidth: string;
  isSavingAsset: boolean = false;
  locations: Location[] = [];
  filteredLocations: Location[] = [];
  departments: Department[] = [];
  filteredDepartments: Department[] = [];
  legalEntities: LegalEntity[] = [];
  employees: Employee[] = [];
  filteredEmployees: Employee[] = [];

  constructor(
    private assetService: AssetService,
    private locationService: LocationService,
    private departmentService: DepartmentService,
    private legalEntityService: LegalEntityService,
    private employeeService: EmployeeService,
    private documentService: DocumentService,
    private messageService: NzMessageService,
    private resizeService: ResizeService,
  ) {
    this.resizeService.onResize$.subscribe((size) => {
      this.drawerWidth = this.resizeService.getDrawerWidth();
    });
  }

  ngOnInit() {
    if (this.selectedLegalEntityId$) {
      this.selectedLegalEntityId$
        .pipe(takeUntil(this.destroy$))
        .subscribe(newId => {
          this.legalEntityChange(newId);
        });
    }
    this.loadDataForDropdowns();
    if (this.resizeService.currentSize != null) {
      this.drawerWidth = this.resizeService.getDrawerWidth();
    }
  }

  ngAfterViewInit() {
    this.editDrawer.afterOpen.subscribe(() => {
      this.loadDataForDropdowns();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDataForDropdowns() {
    this.locationService.getAllBasic().subscribe((data: Location[]) => {
      this.locations = data;
      this.filteredLocations = data;
      if (this.selectedLegalEntityId != null) {
        this.filteredLocations = data.filter(
          (x) => x.legalEntity.id == this.selectedLegalEntityId
        );
      }
    });
    this.employeeService.getAllBasic().subscribe((data: Employee[]) => {
      this.employees = data;
      this.filteredEmployees = data;
      if (this.selectedLegalEntityId != null) {
        this.filteredEmployees = data.filter(
          (x) => x.department && x.department.legalEntity.id == this.selectedLegalEntityId
        );
      }
    });
    this.legalEntityService.getAllBasic().subscribe((data: LegalEntity[]) => {
      this.legalEntities = data;
    });
    this.departmentService.getAllBasic().subscribe((data: Department[]) => {
      this.departments = data;
      this.filteredDepartments = data;
      if (this.selectedLegalEntityId != null) {
        this.filteredDepartments = data.filter(
          (x) => x.legalEntity.id == this.selectedLegalEntityId
        );
      }
    });
  }

  legalEntityChange(legalEntityId: number | null) {
    this.selectedLegalEntityId = legalEntityId;
    this.legalEntityDropdownChange();
  }

  legalEntityDropdownChange() {
    if (this.selectedLegalEntityId != null) {
      this.filteredDepartments = this.departments.filter(
        (x) => x.legalEntity.id == this.selectedLegalEntityId
      );
      this.filteredLocations = this.locations.filter(
        (x) => x.legalEntity.id == this.selectedLegalEntityId
      );
      this.filteredEmployees = this.employees.filter(
        (x) => x.department && x.department.legalEntity.id == this.selectedLegalEntityId
      );
      if (
        this.assetViewModel
      ) {
        if (this.assetViewModel.departmentId != null &&
          this.assetViewModel.departmentId != -1) {
          let department = this.departments.find(
            (x) => x.id == this.assetViewModel.departmentId
          );
          if (department.legalEntity.id != this.selectedLegalEntityId) {
            let defaultDepartment = this.filteredDepartments.find(
              x => x.isDefaultForUnassigned
            );
            if (defaultDepartment) {
              this.assetViewModel.departmentId = defaultDepartment.id;
            }
            else {
              this.assetViewModel.departmentId = null;
            }
          }
        }
        else {
          let defaultDepartment = this.filteredDepartments.find(
            x => x.isDefaultForUnassigned
          );
          if (defaultDepartment) {
            this.assetViewModel.departmentId = defaultDepartment.id;
          }
          else {
            this.assetViewModel.departmentId = null;
          }
        }

        if (this.assetViewModel.locationId != null &&
          this.assetViewModel.locationId != -1) {
          let location = this.locations.find(
            (x) => x.id == this.assetViewModel.departmentId
          );
          if (location.legalEntity.id != this.selectedLegalEntityId) {
            let defaultLocation = this.filteredLocations.find(
              x => x.isDefaultForUnassigned
            );
            if (defaultLocation) {
              this.assetViewModel.locationId = defaultLocation.id;
            }
            else {
              this.assetViewModel.locationId = null;
            }
          }
        }
        else {
          let defaultLocation = this.filteredLocations.find(
            x => x.isDefaultForUnassigned
          );
          if (defaultLocation) {
            this.assetViewModel.locationId = defaultLocation.id;
          }
          else {
            this.assetViewModel.locationId = null;
          }
        }

        if (this.assetViewModel.employeeId != null &&
          this.assetViewModel.employeeId != -1) {
          let employee = this.employees.find(
            (x) => x.id == this.assetViewModel.employeeId
          );
          if (employee.department.legalEntity.id != this.selectedLegalEntityId) {
            this.assetViewModel.employeeId = null;
          }
        }
        else {
          this.assetViewModel.employeeId = null;
        }
      }
      else {

      }
    }
  }


  isFieldHidden(fieldName: string): boolean {
    // Assuming all fields should be visible in this case
    return true;
  }

  closeDrawer(form?: NgForm, reload?: boolean) {
    if (form) {
      form.reset();
    }
    this.assetViewModel = null;
    this.isVisible = false;
    if (reload != null) {
      this.drawerClosed.emit(reload);
    }
    this.drawerClosed.emit(false);
  }

  saveAsset(form: NgForm) {
    if (form.valid) {
      this.isSavingAsset = true;

      this.documentService.saveUnassignmentDocument(this.assetViewModel).subscribe((result: any) => {
        this.messageService.create("success", "Imovina uspješno razdužena");
        
        this.isSavingAsset = false;

        if (result.type == 'application/pdf') {
          var blob = new Blob([result], { type: "application/pdf" });
          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob);
          }
          else {
            var fileUrl = URL.createObjectURL(blob);
            window.open(fileUrl, '_blank');
          }
          this.closeDrawer(this.createForm, true);
        }
      }, error => {
        this.isSavingAsset = false;
      });
    }
  }
}
